import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	inject,
	Input,
	OnDestroy,
	OnInit,
	viewChild,
	ChangeDetectionStrategy,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { CardComponent } from "common/card/card.component";
import { MutexService } from "common/concurrency/mutex.service";
import { DataViewComponent, Mode } from "common/data/data-view.component";
import { PageableDataSource } from "common/data/pageable-data-source.logic";
import { DialogService } from "common/dialog/dialog.service";
import { ViewService } from "core/view.service";
import { UserAssignmentsStore } from "process/userassignments/user-assignments-store.logic";
import { UserAssignmentsService } from "process/userassignments/user-assignments.service";
import { BaseComponent } from "process/view/base.component";
import { ComponentService } from "process/view/component.service";
import { lastValueFrom, Observable, Subscription } from "rxjs";
import { take } from "rxjs/operators";
import { AsyncPipe } from "@angular/common";
import { UserImageComponent } from "common/user-image.component";
import { TranslateModule } from "@ngx-translate/core";
import { TrimDirective } from "common/trim.directive";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { KeyUpDirective } from "common/key-up.directive";
import { MenuComponent } from "common/menu/menu.component";
import { ResponsiveDataViewComponent } from "common/data/responsive-data-view.component";

@Component({
	selector: "stages-user-assignments",
	templateUrl: "./user-assignments.component.html",
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		UserImageComponent,
		TranslateModule,
		TrimDirective,
		ReactiveFormsModule,
		FormsModule,
		KeyUpDirective,
		MenuComponent,
		ResponsiveDataViewComponent,
		AsyncPipe,
	],
})
export class UserAssignmentsComponent extends BaseComponent implements OnInit, OnDestroy {
	private card = inject(CardComponent);
	private route = inject(ActivatedRoute);
	private viewService = inject(ViewService);
	private userAssignmentsService = inject(UserAssignmentsService);
	private mutexService = inject(MutexService);
	private dialogService = inject(DialogService);
	private router = inject(Router);
	private chRef = inject(ChangeDetectorRef);

	UserAssignmentsComponent = UserAssignmentsComponent;
	// TODO: Skipped for migration because:
	//  Your application code writes to the input. This prevents migration.
	@Input()
	path?: string;

	// TODO: Skipped for migration because:
	//  Your application code writes to the input. This prevents migration.
	@Input()
	pageSize: number = 7;

	// TODO: Skipped for migration because:
	//  Your application code writes to the input. This prevents migration.
	@Input()
	elementPageSize: number = 2;

	// TODO: Skipped for migration because:
	//  Your application code writes to the input. This prevents migration.
	@Input()
	translateNone?: string;

	// TODO: Skipped for migration because:
	//  Your application code writes to the input. This prevents migration.
	@Input()
	showUsername?: boolean;

	readonly dataView = viewChild.required<DataViewComponent<stages.userassignments.UserAssignment>>("dataView");

	readonly commentInput = viewChild.required("commentInput", { read: ElementRef });

	dataSource!: PageableDataSource<stages.userassignments.UserAssignment>;
	menuItems: MenuItem[] = [];
	commentInputText!: string;
	editedAssignment!: string;
	self$!: Observable<stages.process.ProcessView>;
	Mode = Mode;

	private subscription!: Subscription;

	constructor() {
		const componentService = inject(ComponentService);

		super(componentService);
	}

	ngOnInit(): void {
		this.self$ = this.viewService.awaitSelfElementObservable();
		this.dataSource = new PageableDataSource(
			new UserAssignmentsStore(
				lastValueFrom(this.self$.pipe(take(1))),
				this.userAssignmentsService,
				this.path ?? null,
				this.pageSize,
				this.elementPageSize,
			),
			this.mutexService,
			this.router,
			this.route,
		);

		// prevent ExpressionChangedAfterItHasBeenCheckedError
		setTimeout(() => {
			this.subscription = this.self$.subscribe((view) => {
				this.card.menuItems = [
					{
						name: "add",
						iconClass: "ico ico-add",
						disabled: () => {
							return this.isCollected() || !view.userContainer || !view.userContainer.actions.AddUserAssignmentPolicy;
						},
						on: () => {
							this.dataView().enterAdd(false);
						},
					},
					{
						name: "delete",
						iconClass: "ico ico-delete",
						disabled: () => {
							return (
								this.isCollected() || !view.userContainer || !view.userContainer.actions.DeleteUserAssignmentPolicy
							);
						},
						on: () => {
							this.dataView().enterDelete();
						},
					},
				];

				this.menuItems = [
					{
						name: "process.element.userassignments.deputy",
						isSwitch: true,
						disabled: (userAssignment: stages.userassignments.UserAssignment) => {
							return this.isCollected() || !userAssignment.actions.SetUserAssignmentDeputyPolicy;
						},
						model: "elements[0].deputy",
						getValue: (assignment: stages.userassignments.UserAssignment) => {
							return assignment.elements[0].deputy;
						},
						onValueChange: (value: boolean, assignment: stages.userassignments.UserAssignment) => {
							this.userAssignmentsService
								.updateDeputy(
									this.route.snapshot.paramMap.get("type")!,
									// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Remove this comment and fix warnings!
									(view as any).elementId,
									this.route.snapshot.paramMap.get("identity")!,
									assignment.elements[0].guid,
									this.route.snapshot.paramMap.get("workspaceId")!,
									this.route.snapshot.paramMap.get("processVersion")!,
									value,
								)
								.then(() => {
									assignment.elements[0].deputy = value;
								});
						},
					},
					{
						isSeparator: true,
					},
					{
						name: "editComment",
						iconClass: "ico ico-edit",
						disabled: (userAssignment: stages.userassignments.UserAssignment) => {
							return this.isCollected() || !userAssignment.actions.SetUserAssignmentCommentPolicy;
						},
						on: (assignment: stages.userassignments.UserAssignment) => {
							this.dataView().mode = Mode.EDIT;
							this.editedAssignment = assignment.user.id;
							this.commentInputText = assignment.elements[0].comment!;

							this.chRef.detectChanges();
							this.commentInput().nativeElement.focus();
						},
					},
					{
						name: "delete",
						iconClass: "ico ico-delete",
						disabled: (userAssignment: stages.userassignments.UserAssignment) => {
							return this.isCollected() || !userAssignment.actions.DeleteUserAssignmentPolicy;
						},
						on: async (deletedAssignment: stages.userassignments.UserAssignment) => {
							if (
								await this.dialogService.confirm(
									"process.element.userassignments.unassign.confirm",
									{ name: deletedAssignment.user.fullname },
									"delete",
									"cancel",
									true,
								)
							) {
								const guid: string = deletedAssignment.elements[0].guid;
								this.dataView().dataSource.delete([guid], guid);
							}
						},
					},
				];

				this.dataView().buttons = [
					{
						class: "button sm cancel",
						translate: "cancel",
						click: () => {
							this.dataView().mode = Mode.VIEW;
						},
						visible: () => {
							return this.dataView().mode === Mode.DELETE;
						},
						disabled: () => {
							return false;
						},
					},
					{
						class: "button sm delete",
						translate: "delete",
						click: () => {
							void this.dataView().dataSource.delete(this.dataView().selection.selected, "userAssignments");
							this.dataView().mode = Mode.VIEW;
						},
						visible: () => {
							return this.dataView().mode === Mode.DELETE;
						},
						disabled: () => {
							return this.dataView().selection.isEmpty();
						},
					},
				];
			});
		});
	}

	ngDestroy(): void {
		if (this.dataSource) {
			this.dataSource.disconnect();
		}

		if (this.subscription) {
			this.subscription.unsubscribe();
		}
	}

	isCollected(): boolean {
		return this.path !== undefined;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Remove this comment and fix warnings!
	hasEmail(user: any): boolean {
		return user.emailAddress !== null;
	}

	isCreatorKnown(userAssignment: stages.userassignments.UserAssignment): boolean {
		return userAssignment.creator !== null && userAssignment.creationDate !== null;
	}

	onCommentInputBlur(): void {
		this.dataView().mode = Mode.VIEW;
		this.editedAssignment = "";
	}

	async onCommentInputKeyUp(
		event: KeyboardEvent,
		assignment: stages.userassignments.UserAssignment,
		elementId: string,
	): Promise<void> {
		if (event.keyCode === 27) {
			this.onCommentInputBlur();
			this.commentInputText = "";
		} else if (event.keyCode === 13) {
			await this.userAssignmentsService.updateComment(
				this.route.snapshot.paramMap.get("type")!,
				elementId,
				this.route.snapshot.paramMap.get("identity")!,
				assignment.elements[0].guid,
				this.route.snapshot.paramMap.get("workspaceId")!,
				this.route.snapshot.paramMap.get("processVersion")!,
				this.commentInputText,
			);
			assignment.elements[0].comment = this.commentInputText;
			this.commentInputText = "";
			this.dataView().mode = Mode.VIEW;
			this.onCommentInputBlur();
		}
	}

	static getId(userAssignment: stages.userassignments.UserAssignment): string {
		return userAssignment.elements[0].guid;
	}

	static isDisabled(): boolean {
		return false;
	}
}
